import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import MuiTextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ModalDialog from '../UI/Dialog/ModalDialog';
import { encryptString } from '../../helpers/securityHelper';

const MIN_LENGTH = 8;
const MAX_LENGTH = 15;

function getPasswordStrength(password) {
  if (!password) return { score: 0, label: '', color: 'error' };
  let score = 0;
  if (password.length >= MIN_LENGTH) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  if (score <= 2) return { score: 16, label: 'weak', color: 'error' };
  if (score <= 3) return { score: 40, label: 'fair', color: 'warning' };
  if (score <= 4) return { score: 66, label: 'good', color: 'info' };
  if (score <= 5) return { score: 85, label: 'strong', color: 'success' };
  return { score: 100, label: 'excellent', color: 'success' };
}

const ruleIconSx = { fontSize: 16 };

function PasswordRule({ met, label }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      {met
        ? <CheckCircleIcon color="success" sx={ruleIconSx} />
        : <CancelIcon color="disabled" sx={ruleIconSx} />
      }
      <Typography
        variant="caption"
        sx={{ color: met ? 'success.main' : 'text.secondary' }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

export default function ChangePasswordDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const userId = useSelector(state => state.auth.user?.id);
  const { t } = useTranslation();

  const [originalPassword, setOriginalPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const strength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);

  const rules = useMemo(() => ({
    length: newPassword.length >= MIN_LENGTH && newPassword.length <= MAX_LENGTH,
    lowercase: /[a-z]/.test(newPassword),
    uppercase: /[A-Z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[^a-zA-Z0-9]/.test(newPassword),
    match: newPassword.length > 0 && confirmPassword.length > 0 && newPassword === confirmPassword,
  }), [newPassword, confirmPassword]);

  const resetForm = useCallback(() => {
    setOriginalPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setErrors({});
    setSaving(false);
    setShowOriginal(false);
    setShowNew(false);
    setShowConfirm(false);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit = useCallback(() => {
    const errs = {};
    if (!originalPassword) errs.originalPassword = t('required');
    if (!newPassword) {
      errs.newPassword = t('required');
    } else if (newPassword.length < MIN_LENGTH || newPassword.length > MAX_LENGTH) {
      errs.newPassword = t('passwordLength');
    }
    if (!confirmPassword) {
      errs.confirmPassword = t('required');
    } else if (newPassword !== confirmPassword) {
      errs.confirmPassword = t('passwordNotMatch');
    }
    if (originalPassword && newPassword && originalPassword === newPassword) {
      errs.newPassword = t('passwordOrigError');
    }
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSaving(true);
    const data = {
      _id: userId,
      password: encryptString(originalPassword),
      newPassword: encryptString(newPassword),
    };
    axios.post('/api/user/changepassword', data)
      .then(result => {
        dispatch({ type: 'CREATE_ALERT', payload: { message: result.data.message, title: t('success'), severity: 'success' } });
        handleClose();
      })
      .catch(err => {
        dispatch({ type: 'CREATE_ALERT', payload: { message: err.response?.data || t('error'), title: t('error'), severity: 'error' } });
        setSaving(false);
      });
  }, [originalPassword, newPassword, confirmPassword, userId, dispatch, t, handleClose]);

  const visibilityToggle = (show, setShow) => (
    <InputAdornment position="end">
      <IconButton onClick={() => setShow(!show)} edge="end" size="small" tabIndex={-1}>
        {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  const buttonStack = (
    <Stack direction="row" spacing={1.5} sx={{ width: '100%', justifyContent: 'flex-end', px: 1 }}>
      <Button variant="outlined" onClick={handleClose} disabled={saving}>
        {t('cancel')}
      </Button>
      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={saving}
        startIcon={<LockResetIcon />}
      >
        {t('changePassword')}
      </Button>
    </Stack>
  );

  return (
    <ModalDialog
      dialogStatus={open}
      toggleDialog={handleClose}
      title={t('changePassword')}
      maxWidth="xs"
      fullWidth
      disableClickAway
      buttonStack={buttonStack}
    >
      <Stack spacing={2.5}>
        {/* Current Password */}
        <MuiTextField
          label={t('originalPassword')}
          type={showOriginal ? 'text' : 'password'}
          value={originalPassword}
          onChange={e => setOriginalPassword(e.target.value)}
          autoComplete="current-password"
          error={!!errors.originalPassword}
          helperText={errors.originalPassword}
          fullWidth
          size="small"
          slotProps={{
            input: {
              endAdornment: visibilityToggle(showOriginal, setShowOriginal),
            },
          }}
        />

        {/* New Password */}
        <Box>
          <MuiTextField
            label={t('newPassword')}
            type={showNew ? 'text' : 'password'}
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            autoComplete="new-password"
            error={!!errors.newPassword}
            helperText={errors.newPassword}
            fullWidth
            size="small"
            slotProps={{
              input: {
                endAdornment: visibilityToggle(showNew, setShowNew),
              },
            }}
          />

          {/* Strength meter */}
          {newPassword.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('passwordStrength') || 'Password strength'}
                </Typography>
                <Chip
                  label={strength.label}
                  color={strength.color}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}
                />
              </Stack>
              <LinearProgress
                variant="determinate"
                value={strength.score}
                color={strength.color}
                sx={{ height: 6, borderRadius: 3 }}
              />

              {/* Rules checklist */}
              <Box sx={{
                mt: 1.5,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'action.hover',
              }}>
                <Stack spacing={0.5}>
                  <PasswordRule met={rules.length} label={`${MIN_LENGTH}-${MAX_LENGTH} ${t('characters') || 'characters'}`} />
                  <PasswordRule met={rules.lowercase} label={t('lowercaseLetter') || 'Lowercase letter (a-z)'} />
                  <PasswordRule met={rules.uppercase} label={t('uppercaseLetter') || 'Uppercase letter (A-Z)'} />
                  <PasswordRule met={rules.number} label={t('number') || 'Number (0-9)'} />
                  <PasswordRule met={rules.special} label={t('specialCharacter') || 'Special character (!@#$...)'} />
                </Stack>
              </Box>
            </Box>
          )}
        </Box>

        {/* Confirm Password */}
        <Box>
          <MuiTextField
            label={t('repeatPassword')}
            type={showConfirm ? 'text' : 'password'}
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            fullWidth
            size="small"
            slotProps={{
              input: {
                endAdornment: visibilityToggle(showConfirm, setShowConfirm),
              },
            }}
          />
          {confirmPassword.length > 0 && newPassword.length > 0 && (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75, ml: 0.5 }}>
              {rules.match
                ? <CheckCircleIcon color="success" sx={ruleIconSx} />
                : <CancelIcon color="error" sx={ruleIconSx} />
              }
              <Typography variant="caption" sx={{ color: rules.match ? 'success.main' : 'error.main' }}>
                {rules.match ? (t('passwordsMatch') || 'Passwords match') : t('passwordNotMatch')}
              </Typography>
            </Stack>
          )}
        </Box>
      </Stack>
    </ModalDialog>
  );
}
